import {
  campaignApiDelete,
  campaignApiGet,
  campaignApiPatch,
  campaignApiPost,
} from '@/lib/api/campaign-api-client';
import type {
  CampaignApiEnvelope,
  SegmentContactSummaryApi,
  SegmentDetailApi,
  SegmentFieldDefinitionApi,
  SegmentListItemApi,
  SegmentPreviewApi,
} from '../types/segments-api.types';
import type {
  CreateSegmentInput,
  GetSegmentsParams,
  Segment,
  SegmentPreviewResult,
  SegmentRuleGroup,
  SegmentsPagingResult,
  UpdateSegmentInput,
} from '../types/segments.types';
import {
  mapSegmentApiToSegment,
  mapSegmentItems,
  toSegmentsPagingResult,
} from '../utils/segment-api-mapper';
import { toWireRuleGroup } from '../utils/segment-rule-tree';

type SegmentPageInfoApi = {
  limit: number;
  offset: number;
  total: number;
  count?: number;
  hasNext?: boolean;
  hasPrev?: boolean;
};

type SegmentsPageApi = {
  items: SegmentListItemApi[];
  pageInfo: SegmentPageInfoApi;
};

export type SegmentContactsPageInfo = {
  limit: number;
  after?: string | null;
  nextAfter?: string | null;
  hasNext: boolean;
};

export type SegmentContactsResult = {
  items: SegmentContactSummaryApi[];
  pageInfo: SegmentContactsPageInfo;
};

const assertCampaignApiSuccess = <T>(
  response: CampaignApiEnvelope<T> | null | undefined,
  errorLabel: string
): T => {
  if (!response || typeof response !== 'object') {
    throw new Error(`${errorLabel}: invalid response structure`);
  }

  if (response.success === false) {
    const message =
      response.message ||
      (Array.isArray(response.errors) ? response.errors.join(', ') : undefined) ||
      errorLabel;
    throw new Error(message);
  }

  if (response.data === undefined || response.data === null) {
    throw new Error(`${errorLabel}: missing data payload`);
  }

  return response.data;
};

const requireSegmentId = (segmentId: string): string => {
  const id = segmentId.trim();
  if (!id) {
    throw new Error('Segment id is required');
  }
  return id;
};

export const getSegmentFields = async (): Promise<SegmentFieldDefinitionApi[]> => {
  const response =
    await campaignApiGet<CampaignApiEnvelope<{ fields: SegmentFieldDefinitionApi[] }>>(
      '/v1/segments/fields'
    );
  return assertCampaignApiSuccess(response, 'Failed to fetch segment fields').fields;
};

export const getSegments = async (
  params: GetSegmentsParams = {}
): Promise<SegmentsPagingResult> => {
  const pageNo = params.pageNo ?? 1;
  const pageSize = params.pageSize ?? 25;
  const offset = Math.max(0, (pageNo - 1) * pageSize);
  const q = params.q?.trim() || undefined;
  const sort = params.sort ?? 'updated';
  const order = params.order ?? 'desc';

  const response = await campaignApiGet<CampaignApiEnvelope<SegmentsPageApi>>('/v1/segments', {
    limit: pageSize,
    offset,
    sort,
    order,
    q,
  });
  const data = assertCampaignApiSuccess(response, 'Failed to fetch segments');

  return toSegmentsPagingResult(mapSegmentItems(data.items), pageNo, pageSize, data.pageInfo);
};

export const getSegmentById = async (segmentId: string): Promise<Segment> => {
  const id = requireSegmentId(segmentId);
  const response = await campaignApiGet<CampaignApiEnvelope<SegmentDetailApi>>(
    `/v1/segments/${encodeURIComponent(id)}`
  );
  const data = assertCampaignApiSuccess(response, 'Failed to fetch segment');
  return mapSegmentApiToSegment(data);
};

export const createSegment = async (input: CreateSegmentInput): Promise<Segment> => {
  const response = await campaignApiPost<CampaignApiEnvelope<SegmentDetailApi>>('/v1/segments', {
    name: input.name,
    description: input.description,
    rules: toWireRuleGroup(input.rules),
  });
  const data = assertCampaignApiSuccess(response, 'Failed to create segment');
  return mapSegmentApiToSegment(data);
};

export const updateSegment = async (
  segmentId: string,
  input: UpdateSegmentInput
): Promise<Segment> => {
  const id = requireSegmentId(segmentId);
  const body: UpdateSegmentInput = {};
  if (input.name !== undefined) body.name = input.name;
  if (input.description !== undefined) body.description = input.description;
  if (input.rules !== undefined) body.rules = toWireRuleGroup(input.rules);

  const response = await campaignApiPatch<CampaignApiEnvelope<SegmentDetailApi>>(
    `/v1/segments/${encodeURIComponent(id)}`,
    body
  );
  const data = assertCampaignApiSuccess(response, 'Failed to update segment');
  return mapSegmentApiToSegment(data);
};

export const deleteSegment = async (segmentId: string): Promise<true> => {
  const id = requireSegmentId(segmentId);
  const response = await campaignApiDelete<CampaignApiEnvelope<unknown> | null>(
    `/v1/segments/${encodeURIComponent(id)}`
  );

  if (response === null) return true;
  if (!response || typeof response !== 'object') {
    throw new Error('Failed to delete segment: invalid response structure');
  }
  if (response.success === false) {
    const message =
      response.message ||
      (Array.isArray(response.errors) ? response.errors.join(', ') : undefined) ||
      'Failed to delete segment';
    throw new Error(message);
  }

  return true;
};

export const previewSegmentRules = async (input: {
  rules: SegmentRuleGroup;
  sampleSize?: number;
}): Promise<SegmentPreviewResult> => {
  const response = await campaignApiPost<CampaignApiEnvelope<SegmentPreviewApi>>(
    '/v1/segments/preview',
    {
      rules: toWireRuleGroup(input.rules),
      sampleSize: input.sampleSize,
    }
  );
  return assertCampaignApiSuccess(response, 'Failed to preview segment');
};

export const previewSavedSegment = async (segmentId: string): Promise<SegmentPreviewResult> => {
  const id = requireSegmentId(segmentId);
  const response = await campaignApiGet<CampaignApiEnvelope<SegmentPreviewApi>>(
    `/v1/segments/${encodeURIComponent(id)}/preview`
  );
  return assertCampaignApiSuccess(response, 'Failed to preview segment');
};

export const getSegmentContacts = async (
  segmentId: string,
  params: { limit?: number; after?: string } = {}
): Promise<SegmentContactsResult> => {
  const id = requireSegmentId(segmentId);
  const response = await campaignApiGet<CampaignApiEnvelope<SegmentContactsResult>>(
    `/v1/segments/${encodeURIComponent(id)}/contacts`,
    {
      limit: params.limit ?? 50,
      after: params.after,
    }
  );
  return assertCampaignApiSuccess(response, 'Failed to fetch segment contacts');
};
